import { useCallback, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { createElement } from 'react';
import { ConfirmDialog } from '@/shared/ui';
import { useLayerCloseGuard } from './useLayerCloseGuard';

interface UseCloseGuardOptions {
  title: string;
  message: string;
  confirmLabel: string;
  cancelLabel: string;
}

interface UseCloseGuardResult {
  /** Render this node anywhere inside the component's JSX (uses a portal). */
  dialogNode: ReactNode;
}

/**
 * Registers a navigation close guard for the given layer.
 * When the layer is about to close and isBlocked is true, shows a ConfirmDialog.
 * Returns a dialogNode that must be rendered somewhere in the component's JSX.
 */
export function useCloseGuard(
  layerId: string,
  isBlocked: boolean,
  options: UseCloseGuardOptions
): UseCloseGuardResult {
  type GuardResolve = (result: boolean) => void;
  const [guardResolve, setGuardResolve] = useState<GuardResolve | null>(null);
  const guardResolveRef = useRef<GuardResolve | null>(null);
  guardResolveRef.current = guardResolve;

  const isBlockedRef = useRef(isBlocked);
  isBlockedRef.current = isBlocked;

  const guard = useCallback((): Promise<boolean> => {
    if (!isBlockedRef.current) return Promise.resolve(true);
    if (guardResolveRef.current !== null) return Promise.resolve(false);
    return new Promise<boolean>((resolve) => {
      setGuardResolve(() => resolve);
    });
  }, []);

  useLayerCloseGuard(layerId, guard);

  const dialogNode: ReactNode =
    guardResolve !== null
      ? createElement(ConfirmDialog, {
          title: options.title,
          message: options.message,
          confirmLabel: options.confirmLabel,
          cancelLabel: options.cancelLabel,
          confirmVariant: 'danger' as const,
          onConfirm: () => {
            const r = guardResolve;
            setGuardResolve(null);
            r(true);
          },
          onCancel: () => {
            const r = guardResolve;
            setGuardResolve(null);
            r(false);
          },
        })
      : null;

  return { dialogNode };
}
